/**
 * What: The milestone checklist for one running test, driven only by the server-confirmed stage.
 * Transport / data-flow: Receives `TestsIndexStage` from the record; renders done / current / not started.
 *   No time estimate and no per-stage percentage.
 */
import { shopifyComponentCopy } from '@testfeed/product-language'
import { TESTS_INDEX_STAGES, type TestsIndexStage } from '../types'

type MilestoneState = 'done' | 'current' | 'upcoming'

/** Merchant label for each milestone, owned by product-language. */
export function stageLabel(stage: TestsIndexStage): string {
  const copy = shopifyComponentCopy.testsIndex
  const labels: Record<TestsIndexStage, string> = {
    waiting: copy.stageWaiting,
    preparing_shoppers: copy.stagePreparingShoppers,
    asking: copy.stageAsking,
    scoring: copy.stageScoring,
    writing_report: copy.stageWritingReport,
  }
  return labels[stage]
}

function milestoneState(index: number, currentIndex: number): MilestoneState {
  if (index < currentIndex) return 'done'
  if (index === currentIndex) return 'current'
  return 'upcoming'
}

function MilestoneIcon({ state }: { state: MilestoneState }) {
  if (state === 'done') return <s-icon type="check-circle" tone="success"></s-icon>
  if (state === 'current') {
    return <s-spinner size="base" accessibilityLabel={shopifyComponentCopy.testsIndex.stageInProgressLabel}></s-spinner>
  }
  return <s-icon type="circle-dashed" tone="neutral"></s-icon>
}

/**
 * The five labels fit on one line only in a wide card. Below this width (for example, two cards side by side on the
 * Tests page) the steps stack one per line instead of wrapping unevenly.
 */
const ONE_LINE_MIN_WIDTH = 960

export function RunMilestones({ stage }: { stage: TestsIndexStage }) {
  const currentIndex = TESTS_INDEX_STAGES.indexOf(stage)
  const narrow = `@container (inline-size < ${ONE_LINE_MIN_WIDTH}px)`
  // Spelled out, not `repeat(5, auto)`: the responsive value syntax splits on commas.
  const oneLine = TESTS_INDEX_STAGES.map(() => 'auto').join(' ')
  return (
    <s-query-container>
      <s-grid
        gridTemplateColumns={`${narrow} 1fr, ${oneLine}`}
        justifyContent="start"
        gap={`${narrow} small-200, large-200`}
        accessibilityLabel={shopifyComponentCopy.testsIndex.stageListLabel}
      >
        {TESTS_INDEX_STAGES.map((milestone, index) => {
          const state = milestoneState(index, currentIndex)
          const textColor = state === 'upcoming' ? 'subdued' : undefined
          return (
            <s-stack key={milestone} direction="inline" gap="small-200" alignItems="center">
              <MilestoneIcon state={state} />
              <s-text color={textColor}>{stageLabel(milestone)}</s-text>
            </s-stack>
          )
        })}
      </s-grid>
    </s-query-container>
  )
}
